import { PageContainer } from "../../../components/pageContainer/PageContainer";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
} from "@sellar-io/components";
import { ReactElement } from "react";
import { useNavigate } from "react-router";
import { Icon, SpinnerLegacy } from "../../../components";
import { AddressFragment } from "../../../components/addressFragment/AddressFragment";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import * as ROUTES from "../../../constants/routes";
import { useSellarUser } from "../../../hooks";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useBusiness } from "../../../hooks/api/v2/business/useBusiness";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { BusinessBankSettingsCard } from "./businessBankSettings/BusinessBankSettingsCard";
import { BusinessSupplierInvoiceCreationStageSettingsCard } from "./businessSupplierInvoiceCreationStageSettings/businessSupplierInvoiceCreationStageSettingsCard";
import { BusinessUserSettings } from "./businessUserSettings/BusinessUserSettings";

export const BusinessSettings = (): ReactElement => {
  const { business: userBusiness } = useSellarCurrentBusiness();

  if (!userBusiness) throw new Error("Cannot find business");

  const collectionAddressCreationEnabled = useRemoteFeatureFlags().isActive(
    "collectionAddressCreation",
  );

  const { user: sellarUser } = useSellarUser();
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();

  const businessQuery = useBusiness(userBusiness.id);

  const showEdit = () =>
    sellarUser?.type == "admin" || businessQuery.data?.id === userBusiness.id;

  return (
    <>
      <NavigationBar
        title={"Business Details"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={
          isTwoPanel ? undefined : () => navigate(ROUTES.BUSINESS)
        }
      />
      {businessQuery.isFetching ||
      businessQuery.data?.id !== userBusiness.id ? (
        <SpinnerLegacy />
      ) : businessQuery.isError ? (
        <ErrorMessage>
          {apiV2AssertError(businessQuery.error)?.response?.data.message}
        </ErrorMessage>
      ) : (
        <PageContainer>
          <Card className="bg-white">
            <CardHeader className="flex items-center justify-between bg-white p-4">
              <div className="flex flex-col">
                <H5>Business details</H5>
                <div className="text-sm text-gray-500">
                  Update your business name, address and other details
                </div>
              </div>
              {showEdit() && (
                <Button onClick={() => navigate(ROUTES.BUSINESS_EDIT_SETTINGS)}>
                  <Icon size={0.7} icon={"PENCIL"} />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className={"flex flex-col gap-4 p-4"}>
              <div className={"flex justify-between"}>
                <div className="grow">
                  <b>Delivery address</b>
                  <div className="flex flex-col select-text">
                    <AddressFragment
                      address={businessQuery.data.DeliveryAddress}
                    />
                  </div>
                </div>
                <div className="grow">
                  <b>Invoice address</b>
                  <div className="flex flex-col select-text">
                    <AddressFragment
                      address={businessQuery.data.InvoiceAddress}
                    />
                  </div>
                </div>
              </div>
              {collectionAddressCreationEnabled && (
                <>
                  {userBusiness.Supplier && (
                    <div className="grow">
                      <b>Collection address</b>
                      <div className="flex flex-col select-text">
                        <AddressFragment
                          address={businessQuery.data.CollectionAddress}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {userBusiness.Retailer && (
                <div>
                  <b>Delivery instructions</b>
                  <div>
                    {businessQuery.data.deliveryInstructions?.length
                      ? businessQuery.data.deliveryInstructions
                      : "No delivery instructions"}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          {businessQuery.data.Supplier?.id && (
            <>
              <BusinessBankSettingsCard
                supplierId={businessQuery.data.Supplier.id}
              />
              <div>
                <BusinessSupplierInvoiceCreationStageSettingsCard
                  supplierId={businessQuery.data.Supplier.id}
                />
              </div>
            </>
          )}
          <BusinessUserSettings />
        </PageContainer>
      )}
    </>
  );
};
